import { createElement, useRef, useMemo, useState } from 'react';
import { Button, ButtonVariant } from '../Button';
import useOnFocusOut from '../../hooks/useOnFocusOut';
import { isNullish } from '@sas/nova-core/typeguards';
import { OptionPopper } from '../OptionPopper';
import { useDesignTokens, renderInlineCssVars } from '../../theme';
import { setAlpha } from '../../design-tokens';
import { ArrowSmallDownBtn } from '@sas/react-svg';
import bundle from './NovaComponents-VerticalBreadcrumb-gui-icu.properties';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { VerticalBreadcrumbProps } from '.';
import { ReactKeyboardEvent } from '../../react-synthetic-events';
import { postfixTestId } from '../../util/testId';

import cn from './VerticalBreadcrumb.css.json';
import './VerticalBreadcrumb.css';
const useTextLocalizer = createUseTextLocalizer(bundle);
const useListId = createUseHtmlId('VerticalBreadcrumbList');

export const VerticalBreadcrumb = (props: VerticalBreadcrumbProps) => {
  const localize = useTextLocalizer();
  const listId = useListId();
  const breadcrumbRef = useRef<HTMLDivElement | null>(null);
  const ulRef = useRef<HTMLUListElement | null>(null);

  const items = useMemo(() => {
    const values = props.values || [];
    return values
      .map((v, i) => ({
        text: v.values[v.selectedIndex],
        originalIndex: i,
      }))
      .reverse();
  }, [props.values]);

  const [focusedIndex, setFocusedIndex] = useState<number | null | undefined>(0);
  const [isOpen, setIsOpen] = useState(false);

  const { onBlur, onFocus } = useOnFocusOut(() => {
    setIsOpen(false);
  });

  const dt = useDesignTokens();
  let tokens;
  let buttonTokens;
  if (dt.enableNovaTokens) {
    tokens = dt.stateInputNova.nonEditable;

    if (dt.themeVariant === 'light' && props.variant === 'primary-light') {
      tokens = {
        fg: dt.c.colorTextInverse,
        bg: setAlpha(dt.c.colorTextInverse, 0.15),
      };
      buttonTokens = renderInlineCssVars({
        'button-bg': setAlpha(dt.c.colorTextInverse, 0.1),
        'button-hover-bg': setAlpha(dt.c.colorTextInverse, 0.15),
        'button-active-bg': setAlpha(dt.c.colorTextInverse, 0.25),
      });
    }
  } else {
    switch (dt.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        tokens = { ...dt.stateStandard.normal, bg: dt.stateSecondary.normal.bg };
        break;
      default:
        switch (dt.themeVariant) {
          case 'dark':
            tokens = { ...dt.stateStandard.normal, bg: setAlpha(dt.c.white, 0.1) };
            break;
          case 'high-contrast':
            tokens = { ...dt.stateStandard.normal, bg: dt.c.white20 };
            break;
          default:
            tokens = { ...dt.stateStandard.normal, bg: setAlpha(dt.c.n2, 0.4) };
            break;
        }
    }
  }
  const spacingTokens = {
    'squish-s': dt.spacing.insetSquish.s.padding,
    'mg-xs': dt.spacing.core.xs,
  };

  const buttonVariant =
    dt.themeVariant === 'light' && props.variant === 'primary-light'
      ? ButtonVariant.Primary
      : ButtonVariant.Standard;

  const tokenOverrides = props.tokens?.();
  if (tokenOverrides?.fg) {
    tokens = { ...tokens, fg: tokenOverrides.fg };
  }

  const outlineOffset = 'calc(-2px + calc(-1 * var(--focus-width)))';
  const style = {
    ...renderInlineCssVars(tokens, 'vertical-field'),
    ...renderInlineCssVars(
      { outline: 'var(--focus-light-bg)', 'outline-offset': outlineOffset },
      'button-focus'
    ),
    ...renderInlineCssVars(
      {
        ...spacingTokens,
        'font-size-m': dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m,
        'font-family': tokenOverrides?.fontFamily ?? 'inherit',
      },
      'breadcrumb'
    ),
  };

  const currentText = items.length ? items[isNullish(focusedIndex) ? 0 : focusedIndex].text : '';

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case ' ':
        // Open dropdown on arrowKey, alt+arrowKey, or space
        if (!e.ctrlKey && !e.shiftKey && !e.metaKey) {
          setIsOpen(true);
          e.preventDefault();
          e.stopPropagation();
        }
        break;
    }
  };

  const closePopover = () => {
    setIsOpen(false);
    setTimeout(() => {
      breadcrumbRef.current?.focus();
    }, 0);
    setFocusedIndex(0);
  };

  const selectBreadcrumb = (index: number) => {
    const selectedItem = items[index];
    props.onPress?.(selectedItem.originalIndex);
  };

  return (
    <div onBlur={onBlur} onFocus={onFocus} style={{ ...style, ...props.style }}>
      <div
        ref={breadcrumbRef}
        onKeyDown={onKeyDown}
        className={cn.vertical}
        role="combobox"
        aria-controls={listId}
        aria-expanded={isOpen}
        aria-readonly
        aria-label={localize('container.aria')}
        aria-haspopup
        tabIndex={0}
        data-testid={postfixTestId(props.testId, 'container')}
      >
        <Button
          onPress={() => setIsOpen((prevIsOpen) => !prevIsOpen)}
          className={cn.verticalButton}
          icon={{ icon: ArrowSmallDownBtn }}
          variant={buttonVariant}
          style={buttonTokens}
          tokens={tokenOverrides?.fg ? () => ({ fg: tokenOverrides?.fg }) : undefined}
          focusable={false}
          aria-hidden
          testId={postfixTestId(props.testId, 'button')}
        />
        <div aria-live={isOpen ? undefined : 'polite'} className={cn.verticalField}>
          {currentText}
        </div>
      </div>
      <OptionPopper
        isOpen={isOpen}
        onClose={closePopover}
        focusedIndex={focusedIndex}
        setFocusedIndex={setFocusedIndex}
        onSelectFocusedItem={() => {
          // NOTE: this fires after the onPress Button event handler
          //     | the isOpen check happens before that event handler's setState calls are committed
          if (isOpen) {
            !isNullish(focusedIndex) && selectBreadcrumb(focusedIndex);
            closePopover();
          }
        }}
        onSelected={({ index }) => {
          selectBreadcrumb(index);
          closePopover();
        }}
        items={items}
        referenceElementRef={breadcrumbRef}
        ulRef={ulRef}
        showSelectedPseudoFocus
        listItemFocusable
        listId={listId}
        listTokens={{
          font: { fontFamily: tokenOverrides?.fontFamily },
          theme: () => ({ fg: tokenOverrides?.fg }),
        }}
        testId={props.testId}
        ariaLabel={localize('list.aria')}
      />
    </div>
  );
};
